import { createContext, useContext, useState, useEffect } from "react";
import {
  getDefaultClient,
  getStoredClient,
  saveClientToStorage,
  removeClientFromStorage,
  hasStoredClient,
} from "@utils/clientUtils";

const AppConfigContext = createContext();

export const useAppConfig = () => {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error("useAppConfig must be used within AppConfigProvider");
  }
  return context;
};

export const AppConfigProvider = ({ children }) => {
  const [appConfig, setAppConfig] = useState(() => {
    const selectedClient = getStoredClient();

    return {
      theme: "light",
      language: "en",
      apiUrl: process.env.REACT_APP_API_URL || "http://localhost:3000",
      selectedClient,
    };
  });

  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("currentUser");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [selectedClient, setSelectedClient] = useState(() => getStoredClient());

  const updateAppConfig = (updates) => {
    setAppConfig((prev) => ({ ...prev, ...updates }));
  };

  const loginUser = (userData) => {
    setUser(userData.data);

    // Get stored client or default
    const clientData = hasStoredClient()
      ? getStoredClient()
      : saveClientToStorage(
          getDefaultClient().clientId,
          getDefaultClient().clientName,
        );

    setSelectedClient(clientData);
    setAppConfig((prev) => ({
      ...prev,
      userData: userData?.data?.user,
      loginToken: userData?.data?.token,
      selectedClient: clientData,
    }));
    localStorage.setItem("currentUser", JSON.stringify(userData?.data));
    localStorage.setItem("loggedin", "true");
  };

  const logoutUser = () => {
    setUser(null);
    const defaultClient = getDefaultClient();
    setSelectedClient(defaultClient);
    setAppConfig((prev) => ({
      ...prev,
      selectedClient: defaultClient,
    }));
    removeClientFromStorage();
    localStorage.clear();
  };

  const updateSelectedClient = (clientId, clientName) => {
    const clientData = saveClientToStorage(clientId, clientName);
    setSelectedClient(clientData);
    setAppConfig((prev) => ({
      ...prev,
      selectedClient: clientData,
    }));
  };

  useEffect(() => {
    // Sync appConfig with user data whenever user changes
    if (user?.user) {
      setAppConfig((prev) => ({
        ...prev,
        userData: user.user,
        loginToken: user.token,
      }));
    }
  }, [user]);

  useEffect(() => {
    // Expose to window in non-production environments
    if (process.env.NODE_ENV !== "production") {
      window.appConfig = appConfig;
    }
  }, [appConfig]);

  const value = {
    appConfig,
    updateAppConfig,
    user,
    loginUser,
    logoutUser,
    selectedClient,
    updateSelectedClient,
  };

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
};
